// Placeholder sound: every effect is synthesised with Web Audio (noise + tones), no files to license or
// load. Ported from prototypes/match/js/sound.js verbatim; the `hero` passed to play() is already the
// resolved Hero (fx.ts resolves the event's id before calling in), so nothing here changed for that.
// Fairness rule: only events the player's team can see make a sound (the caller filters by vision).
import * as THREE from 'three';
import type { ViewHero } from './gameView.js';
import { readSettings, updateSettings } from './settings.js';

export type Sound = ReturnType<typeof createSound>;

export function createSound(camera: THREE.Camera, _stageEl: HTMLElement) {
  let ctx: AudioContext | null = null, master: GainNode | null = null, noise: AudioBuffer | null = null;
  const initial = readSettings();
  let muted = !initial.sound;
  const gain = () => muted ? 0 : 0.55 * initial.volume;
  const lastAt: Record<string, number> = {};

  /* Browsers only start audio after a user gesture. */
  function unlock() {
    if (ctx) { if (ctx.state === 'suspended') ctx.resume(); return; }
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = gain();
    master.connect(ctx.destination);
    noise = ctx.createBuffer(1, ctx.sampleRate * 1.5, ctx.sampleRate);
    const d = noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  /* ---- building blocks: each returns nothing, schedules nodes at `t` into `out` ---- */
  function env(g: GainNode, t: number, a: number, peak: number, dur: number) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  }
  type HissOpts = { dur?: number; type?: BiquadFilterType; f0?: number; f1?: number; q?: number; vol?: number; attack?: number };
  function hiss(out: AudioNode, t: number, opts: HissOpts) {
    const { dur = 0.2, type = 'bandpass', f0 = 1200, f1 = f0, q = 1, vol = 0.6, attack = 0.005 } = opts;
    const src = ctx!.createBufferSource(); src.buffer = noise; src.playbackRate.value = 0.8 + Math.random() * 0.4;
    const bq = ctx!.createBiquadFilter(); bq.type = type; bq.Q.value = q;
    bq.frequency.setValueAtTime(f0, t); bq.frequency.exponentialRampToValueAtTime(Math.max(40, f1), t + dur);
    const g = ctx!.createGain(); env(g, t, attack, vol, dur);
    src.connect(bq); bq.connect(g); g.connect(out);
    src.start(t, Math.random()); src.stop(t + dur + 0.05);
  }
  type ToneOpts = { f0?: number; f1?: number; dur?: number; type?: OscillatorType; vol?: number; attack?: number };
  function tone(out: AudioNode, t: number, opts: ToneOpts) {
    const { f0 = 440, f1 = f0, dur = 0.2, type = 'sine', vol = 0.4, attack = 0.005 } = opts;
    const o = ctx!.createOscillator(); o.type = type;
    o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx!.createGain(); env(g, t, attack, vol, dur);
    o.connect(g); g.connect(out);
    o.start(t); o.stop(t + dur + 0.05);
  }

  // Muzzles for bolt looks that are no powder shot: small, quiet, and none of them Monk's two-tone chime.
  const spitHiss = (o: AudioNode, t: number) => hiss(o, t, { dur: 0.35, type: 'lowpass', f0: 700, f1: 300, vol: 0.35, attack: 0.04 });
  const SOFT_MUZZLE: Record<string, (o: AudioNode, t: number) => void> = {
    claw: spitHiss, crow: spitHiss,
    drop: (o, t) => tone(o, t, { f0: 660, f1: 520, dur: 0.3, vol: 0.14, attack: 0.02 }),               // a falling drop
    sliver: (o, t) => hiss(o, t, { dur: 0.3, type: 'lowpass', f0: 500, f1: 180, vol: 0.22, attack: 0.05 }), // a breath of dusk
    dart: (o, t) => hiss(o, t, { dur: 0.12, f0: 2600, f1: 1200, q: 2, vol: 0.25 }),                       // a light whoosh
    bobbin: (o, t) => tone(o, t, { f0: 440, f1: 300, dur: 0.07, type: 'triangle', vol: 0.3 }),             // a wooden tock
  };

  /* ---- recipes: `p` is a pitch factor (heavier heroes lower), `big` a stronger variant ---- */
  const RECIPES: Record<string, (o: AudioNode, t: number, p: number, big?: boolean, look?: string) => void> = {
    slash: (o, t, p) => { hiss(o, t, { dur: 0.16, f0: 3200 * p, f1: 900 * p, q: 2, vol: 0.5 }); },
    hit: (o, t, p, big) => {
      tone(o, t, { f0: 150 * p, f1: 55 * p, dur: big ? 0.22 : 0.12, vol: big ? 0.7 : 0.45 });
      hiss(o, t, { dur: 0.05, type: 'highpass', f0: 2500, vol: 0.25 });
    },
    muzzle: (o, t, p, big, look) => {
      const soft = look ? SOFT_MUZZLE[look] : undefined;
      if (soft) { soft(o, t); return; }
      if (look === 'smoke' || look === 'spit') { spitHiss(o, t); return; }
      if (look === 'star') { [1320, 1760, 2640].forEach((f, i) => tone(o, t + i * 0.035, { f0: f, dur: 0.18, vol: 0.18 })); if (!big) return; }
      if (look === 'mote') { tone(o, t, { f0: 988, dur: 0.35, vol: 0.2 }); tone(o, t, { f0: 1480, dur: 0.3, vol: 0.12 }); return; }
      hiss(o, t, { dur: big ? 0.5 : 0.28, type: 'lowpass', f0: 2600, f1: 400, vol: big ? 0.9 : 0.6 });
      tone(o, t, { f0: 110, f1: 45, dur: big ? 0.35 : 0.18, vol: 0.6 });
    },
    splat: (o, t, p, big) => { hiss(o, t, { dur: big ? 0.5 : 0.3, type: 'lowpass', f0: 1800, f1: 200, q: 3, vol: 0.45 }); },
    shockwave: (o, t, p, big) => {
      tone(o, t, { f0: 80, f1: 35, dur: big ? 0.6 : 0.4, vol: 0.7 });
      hiss(o, t, { dur: 0.35, type: 'lowpass', f0: 900, f1: 120, vol: 0.4 });
    },
    slam: (o, t) => { RECIPES.shockwave!(o, t, 1, true); RECIPES.block!(o, t + 0.02, 1); },
    // the skill finishes that used to be a bare `shockwave` (and `slam`/`splat`) keep what they sounded like
    ramHit: (o, t, p, big) => { if (big) RECIPES.slam!(o, t, p); else RECIPES.shockwave!(o, t, p, false); },
    leapLand: (o, t, p) => { RECIPES.shockwave!(o, t, p, true); RECIPES.splat!(o, t, p, true); },
    flockLand: (o, t, p) => { RECIPES.shockwave!(o, t, p, true); },
    shadowBurst: (o, t, p) => { RECIPES.shockwave!(o, t, p, false); },
    block: (o, t) => { [820, 1270, 1930].forEach((f, i) => tone(o, t, { f0: f, f1: f * 0.98, dur: 0.35 - i * 0.07, vol: 0.2 })); },
    shatter: (o, t) => {
      hiss(o, t, { dur: 0.3, type: 'highpass', f0: 4000, vol: 0.5 });
      [2100, 3150, 4200].forEach((f, i) => tone(o, t + i * 0.02, { f0: f, dur: 0.15, vol: 0.12 }));
    },
    flame: (o, t) => { hiss(o, t, { dur: 0.22, f0: 700, f1: 1100, q: 0.7, vol: 0.35, attack: 0.02 }); },
    trapPop: (o, t) => { tone(o, t, { f0: 420, f1: 110, dur: 0.12, vol: 0.5 }); RECIPES.splat!(o, t, 1, false); },
    taunt: (o, t) => { RECIPES.block!(o, t, 1); },
    rally: (o, t) => { tone(o, t, { f0: 196, dur: 0.5, type: 'sawtooth', vol: 0.18, attack: 0.03 }); tone(o, t, { f0: 294, dur: 0.5, type: 'sawtooth', vol: 0.12, attack: 0.03 }); RECIPES.block!(o, t, 1); },
    warcry: (o, t) => { [150, 153, 225].forEach(f => tone(o, t, { f0: f, f1: f * 1.15, dur: 0.6, type: 'sawtooth', vol: 0.14, attack: 0.05 })); },
    pact: (o, t) => { tone(o, t, { f0: 92, f1: 70, dur: 0.6, type: 'sawtooth', vol: 0.2, attack: 0.08 }); tone(o, t, { f0: 97, f1: 72, dur: 0.6, type: 'sawtooth', vol: 0.2, attack: 0.08 }); },
    prayer: (o, t) => { [660, 880, 1320].forEach((f, i) => tone(o, t + i * 0.06, { f0: f, dur: 0.6, vol: 0.12, attack: 0.02 })); },
    ward: (o, t) => { tone(o, t, { f0: 740, dur: 0.5, vol: 0.18, attack: 0.02 }); tone(o, t, { f0: 1110, dur: 0.4, vol: 0.1, attack: 0.02 }); },
    wardBreak: (o, t) => { RECIPES.shatter!(o, t, 1); },
    feathers: (o, t) => { for (let i = 0; i < 6; i++) hiss(o, t + i * 0.04, { dur: 0.06, f0: 1500, vol: 0.3 }); },
    sweep: (o, t) => { hiss(o, t, { dur: 0.3, f0: 2200, f1: 500, q: 1.5, vol: 0.6 }); },
    feintArmed: (o, t) => { tone(o, t, { f0: 1200, f1: 1800, dur: 0.2, vol: 0.2 }); },
    capture: (o, t) => { [523, 659, 784].forEach((f, i) => tone(o, t + i * 0.09, { f0: f, dur: 0.7, type: 'triangle', vol: 0.18 })); },
    death: (o, t) => { hiss(o, t, { dur: 1.2, type: 'lowpass', f0: 1200, f1: 80, q: 4, vol: 0.4, attack: 0.1 }); tone(o, t, { f0: 180, f1: 60, dur: 1.0, vol: 0.25 }); },

    /* ---- artefacts — placeholders built from the same recipes above, per the task's own instruction;
       real samples come later. Each aliases or lightly retunes whatever existing recipe the fx.ts visual
       it accompanies already reuses, so ear and eye stay in sync even as placeholders. */
    bloodvial: (o, t, p) => { RECIPES.hit!(o, t, p, false); tone(o, t, { f0: 660, dur: 0.4, vol: 0.15, attack: 0.03 }); },
    windoil: (o, t) => { RECIPES.shockwave!(o, t, 1, false); },
    cloak: (o, t) => { hiss(o, t, { dur: 0.3, type: 'lowpass', f0: 1200, f1: 200, vol: 0.22, attack: 0.05 }); },
    totemPlace: (o, t) => { tone(o, t, { f0: 140, f1: 80, dur: 0.3, vol: 0.4 }); hiss(o, t, { dur: 0.2, type: 'lowpass', f0: 500, vol: 0.2 }); },
    totemBreak: (o, t) => { hiss(o, t, { dur: 0.25, type: 'bandpass', f0: 1800, f1: 400, q: 1.5, vol: 0.4 }); tone(o, t, { f0: 220, f1: 90, dur: 0.2, vol: 0.3 }); },
    /* ---- Астрал: placeholders from the recipes above, like the artefacts' ---- */
    stealth: (o, t) => { RECIPES.cloak!(o, t, 1); },
    shadowSwap: (o, t) => { RECIPES.cloak!(o, t, 1); tone(o, t + 0.05, { f0: 140, f1: 70, dur: 0.25, vol: 0.3 }); },
    charm: (o, t) => { [587, 740].forEach((f, i) => tone(o, t + i * 0.08, { f0: f, f1: f * 0.94, dur: 0.45, type: 'triangle', vol: 0.14, attack: 0.03 })); },
    fear: (o, t) => { RECIPES.feathers!(o, t, 1); tone(o, t, { f0: 220, f1: 110, dur: 0.4, type: 'sawtooth', vol: 0.12 }); },
    atonement: (o, t) => { RECIPES.pact!(o, t, 1); },
    delusion: (o, t) => { [150, 159, 212].forEach(f => tone(o, t, { f0: f, f1: f * 0.85, dur: 0.7, type: 'sawtooth', vol: 0.11, attack: 0.08 })); },
    /* ---- Заклинатели: placeholders from the recipes above ---- */
    summon: (o, t) => { RECIPES.cloak!(o, t, 1); },
    summonGone: (o, t, p) => { RECIPES.splat!(o, t, p, false); },
    tether: (o, t) => { tone(o, t + 0.2, { f0: 330, f1: 180, dur: 0.12, vol: 0.25 }); hiss(o, t + 0.2, { dur: 0.08, type: 'bandpass', f0: 1400, vol: 0.2 }); },  // after the coil's flight
    /* ---- neutral monsters ---- */
    monsterSlain: (o, t) => { tone(o, t, { f0: 70, f1: 32, dur: 1.1, type: 'sawtooth', vol: 0.3, attack: 0.02 }); RECIPES.death!(o, t + 0.05, 1); },
    brand: (o, t, p) => { RECIPES.hit!(o, t, p, false); hiss(o, t, { dur: 0.15, type: 'highpass', f0: 3000, vol: 0.15 }); },
    brandMonster: (o, t, p) => { RECIPES.brand!(o, t, p); },
  };
  const PITCH: Record<string, number> = { knight: 0.75, reaper: 1.0, alchemist: 1.1, doctor: 0.95, outsider: 1.05, summoner: 1.0 };

  const tmp = new THREE.Vector3();
  /* Stereo pan from screen position; quieter the farther from the camera's focus. */
  function play(name: string, pos: { x: number; z: number } | null | undefined, { big = false, hero = null as ViewHero | null } = {}) {
    const recipe = RECIPES[name];
    if (!ctx || muted || !recipe || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    if (now - (lastAt[name] || 0) < 0.04) return;  // many identical events in one frame make one sound
    lastAt[name] = now;
    let pan = 0, vol = 1;
    if (pos) {
      tmp.set(pos.x, 1, pos.z).project(camera);
      pan = Math.max(-1, Math.min(1, tmp.x * 0.8));
      vol = Math.max(0.15, 1 - Math.max(0, Math.hypot(tmp.x, tmp.y) - 0.6) * 0.8);
    }
    const out = ctx.createGain();
    out.gain.value = vol;
    const panner = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    if (panner) { panner.pan.value = pan; out.connect(panner); panner.connect(master!); } else out.connect(master!);
    const p = hero ? (PITCH[hero.def.cls] || 1) * (0.95 + Math.random() * 0.1) : 0.95 + Math.random() * 0.1;
    recipe(out, now + 0.005, p, big, hero ? hero.def.boltLook : undefined);
  }

  function setMuted(m: boolean) {
    muted = m;
    updateSettings({ sound: !m });
    if (master) master.gain.value = gain();
  }
  return { play, setMuted, get muted() { return muted; } };
}
